// Webhooks tab — endpoints that receive this project's events (signing,
// shares). Each has an event filter, an on/off switch and a secret that is
// shown once (on create and on "New secret"); Send test posts a signed ping
// and reports what came back; the delivery log lists every attempt and can
// send one again. Receivers verify the signature as docs/events.md shows.

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, KeyRound, Loader2, Pencil, Plus, RefreshCw, Send, Trash2, Webhook } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useFieldErrors } from '@/shared/hooks/useFieldErrors.js';
import { FieldError, REQUIRED_LABEL } from '@/shared/ui/FieldError.jsx';
import { api, ep } from '../lib/api.js';
import { formatDateTime } from '../lib/format.js';
import { DEFAULT_WEBHOOK_RULES, refusedUrlText, webhookProblems } from '../lib/webhookRules.js';
import { usePagedList } from '../lib/usePagedList.js';
import { ConfirmDialog } from './ConfirmDialog.jsx';
import { ListPagination } from './ListPagination.jsx';

const STATUS_TONE = {
  sent: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
  pending: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  sending: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  dead: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
};

function copyText(text, t) {
  navigator.clipboard?.writeText(text).then(
    () => toast.success(t('webhooks.secret.copied')),
    () => toast.error(t('webhooks.secret.copyFailed')),
  );
}

function SecretDialog({ secret, onClose }) {
  const { t } = useTranslation('console');
  return (
    <Dialog open={Boolean(secret)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('webhooks.secret.title')}</DialogTitle>
          <DialogDescription>{t('webhooks.secret.description')}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 rounded-lg border bg-muted/50 p-3">
          <code className="flex-1 break-all font-mono text-sm" data-testid="webhook-secret">{secret}</code>
          <Button variant="outline" size="icon" className="shrink-0" onClick={() => copyText(secret, t)} aria-label={t('webhooks.secret.copy')}>
            <Copy className="h-4 w-4" />
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>{t('webhooks.secret.done')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WebhookForm({ open, onOpenChange, initial, catalogue, rules = DEFAULT_WEBHOOK_RULES, onSave }) {
  const { t } = useTranslation('console');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const checks = useFieldErrors(webhookProblems({ name, url, types }, rules));
  const { reset } = checks;

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || '');
    setUrl(initial?.url || '');
    setTypes(initial?.event_types || []);
    reset();
  }, [open, initial, reset]);

  const toggle = (type) => {
    setTypes((prev) => (prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type]));
    checks.touch('event_types');
  };

  const submit = async () => {
    if (!checks.attempt()) return;
    setBusy(true);
    try {
      const res = await onSave({ name: name.trim(), url: url.trim(), event_types: types });
      if (res && !res.success) {
        // A refused address says which rule it broke.
        checks.showServer(res, ['name', 'url', 'event_types'], {}, {
          webhook_url_refused: (parsed) => refusedUrlText(parsed.params?.reason),
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t(initial ? 'webhooks.form.editTitle' : 'webhooks.form.createTitle')}</DialogTitle>
          <DialogDescription>{t('webhooks.form.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="webhook-name" className={REQUIRED_LABEL}>{t('webhooks.form.name')}</Label>
            <Input id="webhook-name" required maxLength={rules.name_max} value={name}
              onChange={(e) => { setName(e.target.value); checks.touch('name'); }}
              placeholder={t('webhooks.form.namePlaceholder')} {...checks.describe('name', 'webhook')} />
            <FieldError id="webhook-name-error" message={checks.errors.name} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="webhook-url" className={REQUIRED_LABEL}>{t('webhooks.form.url')}</Label>
            <Input id="webhook-url" type="url" required maxLength={rules.url_max} value={url}
              onChange={(e) => { setUrl(e.target.value); checks.touch('url'); }}
              placeholder={rules.require_https ? 'https://…' : 'https://… / http://…'} {...checks.describe('url', 'webhook')} />
            <FieldError id="webhook-url-error" message={checks.errors.url} />
            <p className="text-xs text-muted-foreground">{t(rules.require_https ? 'webhooks.form.urlHint' : 'webhooks.form.urlHintHttp')}</p>
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('webhooks.form.events')}</legend>
            <p className="text-xs text-muted-foreground">{t('webhooks.form.eventsHint')}</p>
            <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border p-3">
              {catalogue.map((item) => (
                <label key={item.type} className="flex items-start gap-2 text-sm">
                  <Checkbox checked={types.includes(item.type)} onCheckedChange={() => toggle(item.type)} aria-label={item.type} />
                  <span>
                    <span className="font-mono text-xs">{item.type}</span>
                    <span className="block text-xs text-muted-foreground">{item.description}</span>
                  </span>
                </label>
              ))}
            </div>
            <FieldError id="webhook-event_types-error" message={checks.errors.event_types} />
          </fieldset>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('confirm.cancel')}</Button>
          <Button onClick={submit} disabled={busy || !name.trim() || !url.trim() || checks.blocked} data-testid="webhook-save">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('webhooks.form.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Deliveries({ projectId, webhook }) {
  const { t } = useTranslation('console');
  const list = usePagedList(ep.webhookDeliveries(projectId, webhook.id), { size: 25 });
  const [detail, setDetail] = useState(null);

  const open = async (row) => {
    const res = await api.get(ep.webhookDelivery(projectId, webhook.id, row.id));
    if (res.success) setDetail(res.response);
    else toast.error(res.message);
  };
  const redeliver = async (row) => {
    const res = await api.post(ep.webhookRedeliver(projectId, webhook.id, row.id));
    if (res.success) {
      toast.success(t('webhooks.deliveries.redelivered'));
      list.reload();
    } else {
      toast.error(res.message);
    }
  };

  return (
    <div className="space-y-3" data-testid={`webhook-deliveries-${webhook.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Select value={list.filters?.status || 'all'} onValueChange={(v) => list.setFilter('status', v)}>
          <SelectTrigger className="h-8 w-44" aria-label={t('webhooks.deliveries.status')}><SelectValue /></SelectTrigger>
          <SelectContent>
            {['all', 'sent', 'pending', 'dead'].map((s) => (
              <SelectItem key={s} value={s}>{t(`webhooks.deliveries.statuses.${s}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => list.reload()}>
          <RefreshCw className="h-3.5 w-3.5" /> {t('webhooks.deliveries.refresh')}
        </Button>
      </div>
      {list.items.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">{t('webhooks.deliveries.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2">{t('webhooks.deliveries.event')}</th>
                <th className="px-3 py-2">{t('webhooks.deliveries.status')}</th>
                <th className="px-3 py-2">{t('webhooks.deliveries.response')}</th>
                <th className="px-3 py-2">{t('webhooks.deliveries.attempts')}</th>
                <th className="px-3 py-2">{t('webhooks.deliveries.when')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {list.items.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="px-3 py-2">
                    <button type="button" className="font-mono text-xs text-blue-700 dark:text-blue-300 hover:underline" onClick={() => open(row)}>
                      {row.event_type}
                    </button>
                    {row.is_test && <Badge variant="outline" className="ml-2">{t('webhooks.deliveries.test')}</Badge>}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={STATUS_TONE[row.status]}>{t(`webhooks.deliveries.statuses.${row.status}`)}</Badge>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {row.response_status ? `HTTP ${row.response_status}` : row.last_error || '—'}
                    {row.response_ms != null && <span className="ml-1 text-muted-foreground">· {row.response_ms} ms</span>}
                  </td>
                  <td className="px-3 py-2 tabular-nums">{row.attempts}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {formatDateTime(row.sent_at || row.created_at)}
                    {row.status === 'pending' && row.next_attempt_at && (
                      <span className="block">{t('webhooks.deliveries.next', { when: formatDateTime(row.next_attempt_at) })}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!row.is_test && row.status !== 'sending' && (
                      <Button variant="ghost" size="sm" onClick={() => redeliver(row)}>{t('webhooks.deliveries.redeliver')}</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ListPagination list={list} />

      <Dialog open={Boolean(detail)} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-mono text-base">{detail?.event_type}</DialogTitle>
            <DialogDescription>{detail?.event_id}</DialogDescription>
          </DialogHeader>
          {detail?.last_error && <p className="text-sm text-red-600 dark:text-red-400">{detail.last_error}</p>}
          {detail?.response_snippet && (
            <pre className="max-h-24 overflow-auto rounded bg-muted/50 p-2 text-xs">{detail.response_snippet}</pre>
          )}
          <pre className="max-h-96 overflow-auto rounded bg-gray-900 p-3 text-xs text-gray-100" data-testid="delivery-payload">
            {JSON.stringify(detail?.payload, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WebhookCard({ projectId, webhook, onEdit, onChanged, onSecret, onDelete }) {
  const { t } = useTranslation('console');
  const [busy, setBusy] = useState(null);
  const [showLog, setShowLog] = useState(false);

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  const test = () => run('test', async () => {
    const res = await api.post(ep.webhookTest(projectId, webhook.id));
    if (!res.success) return toast.error(res.message);
    const d = res.response;
    if (d.status === 'sent') toast.success(t('webhooks.test.ok', { status: d.response_status, ms: d.response_ms }));
    else toast.error(t('webhooks.test.failed', { reason: d.response_status ? `HTTP ${d.response_status}` : d.last_error }));
    onChanged();
  });
  const rotate = () => run('rotate', async () => {
    const res = await api.post(ep.webhookRotate(projectId, webhook.id));
    if (!res.success) return toast.error(res.message);
    onSecret(res.response.secret);
    onChanged();
  });
  const toggleActive = () => run('active', async () => {
    const res = await api.patch(ep.webhook(projectId, webhook.id), { data: { active: !webhook.active } });
    if (!res.success) return toast.error(res.message);
    onChanged();
  });

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4" data-testid={`webhook-${webhook.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-foreground">{webhook.name}</h3>
            <Badge variant="outline" className={webhook.active ? STATUS_TONE.sent : STATUS_TONE.dead}>
              {t(webhook.active ? 'webhooks.on' : 'webhooks.off')}
            </Badge>
            {webhook.rotating && <Badge variant="outline">{t('webhooks.rotating')}</Badge>}
          </div>
          <p className="break-all font-mono text-xs text-muted-foreground">{webhook.url}</p>
          <p className="text-xs text-muted-foreground">
            {webhook.event_types.length ? webhook.event_types.join(', ') : t('webhooks.allEvents')}
            {' · '}{t('webhooks.secretHint', { hint: webhook.secret_hint })}
          </p>
          {webhook.disabled_reason && <p className="text-xs text-red-600 dark:text-red-400">{webhook.disabled_reason}</p>}
          <p className="text-xs text-muted-foreground">
            {webhook.last_success_at && t('webhooks.lastSuccess', { when: formatDateTime(webhook.last_success_at) })}
            {webhook.last_failure_at && <span className="ml-2">{t('webhooks.lastFailure', { when: formatDateTime(webhook.last_failure_at) })}</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={test} disabled={busy !== null || !webhook.active} data-testid={`webhook-test-${webhook.id}`}>
            {busy === 'test' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {t('webhooks.actions.test')}
          </Button>
          <Button variant="outline" size="sm" onClick={toggleActive} disabled={busy !== null}>
            {t(webhook.active ? 'webhooks.actions.turnOff' : 'webhooks.actions.turnOn')}
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={rotate} disabled={busy !== null}>
            <KeyRound className="h-3.5 w-3.5" /> {t('webhooks.actions.rotate')}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onEdit(webhook)} aria-label={t('webhooks.actions.edit')}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(webhook)} aria-label={t('webhooks.actions.delete')}>
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </Button>
        </div>
      </div>
      <Button variant="link" size="sm" className="h-auto px-0" onClick={() => setShowLog((v) => !v)}>
        {t(showLog ? 'webhooks.deliveries.hide' : 'webhooks.deliveries.show')}
      </Button>
      {showLog && <Deliveries projectId={projectId} webhook={webhook} />}
    </section>
  );
}

export const WebhooksTab = ({ projectId }) => {
  const { t } = useTranslation('console');
  const [hooks, setHooks] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null); // {initial} when open
  const [secret, setSecret] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [rules, setRules] = useState(DEFAULT_WEBHOOK_RULES);

  const load = useCallback(async () => {
    const res = await api.get(ep.webhooks(projectId));
    if (res.success) setHooks(res.response);
    else setError(res.message);
  }, [projectId]);

  useEffect(() => {
    load();
    api.get(ep.webhookCatalogue(projectId)).then((res) => res.success && setCatalogue(res.response));
    api.get(ep.webhookRules(projectId)).then((res) => {
      if (res.success && typeof res.response?.require_https === 'boolean') setRules({ ...DEFAULT_WEBHOOK_RULES, ...res.response });
    });
  }, [load, projectId]);

  // A refusal goes back to the form, which shows it beside its field.
  const save = async (data) => {
    if (form?.initial) {
      const res = await api.patch(ep.webhook(projectId, form.initial.id), { data });
      if (!res.success) return res;
      toast.success(t('webhooks.form.saved'));
    } else {
      const res = await api.post(ep.webhooks(projectId), { data });
      if (!res.success) return res;
      setSecret(res.response.secret);
    }
    setForm(null);
    load();
    return { success: true };
  };

  const remove = async (hook) => {
    const res = await api.del(ep.webhook(projectId, hook.id));
    if (!res.success) return toast.error(res.message);
    toast.success(t('webhooks.delete.done'));
    load();
  };

  if (error) return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!hooks) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-4" data-testid="webhooks">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Webhook className="h-5 w-5 text-muted-foreground" /> {t('webhooks.title')}
          </h2>
          <p className="max-w-2xl text-sm text-muted-foreground">{t('webhooks.description')}</p>
        </div>
        <Button className="gap-2" onClick={() => setForm({ initial: null })} data-testid="webhook-add">
          <Plus className="h-4 w-4" /> {t('webhooks.add')}
        </Button>
      </div>

      {hooks.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">{t('webhooks.empty')}</div>
      ) : (
        hooks.map((hook) => (
          <WebhookCard
            key={hook.id}
            projectId={projectId}
            webhook={hook}
            onEdit={(h) => setForm({ initial: h })}
            onChanged={load}
            onSecret={setSecret}
            onDelete={setPendingDelete}
          />
        ))
      )}

      <WebhookForm
        open={Boolean(form)}
        onOpenChange={(o) => !o && setForm(null)}
        initial={form?.initial}
        catalogue={catalogue}
        rules={rules}
        onSave={save}
      />
      <SecretDialog secret={secret} onClose={() => setSecret(null)} />
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        title={t('webhooks.delete.title')}
        description={t('webhooks.delete.body', { name: pendingDelete?.name ?? '' })}
        confirmLabel={t('webhooks.delete.confirm')}
        destructive
        onConfirm={() => remove(pendingDelete)}
      />
    </div>
  );
};
